import { Link, usePage } from "@inertiajs/react"
import {
  LayoutDashboard,
  FileText,
  ClipboardList,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Users,
  Settings,
  BookOpen,
  TrendingUp,
  Lock,
  Send,
  ShieldCheck,
} from "lucide-react"
import { useState, useEffect } from "react"
import { useMediaQuery } from "@/hooks/use-media-query"
import { inertiaUrlPathname } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import logoBlackText from "@/assets/logos/kf-logo-black-text.webp"
import FeedbackWidget from "@/components/feedback/FeedbackWidget"
import { ImpersonationBanner } from "@/components/ImpersonationBanner"

const navigation = [
  { name: "Dashboard", href: "/app/admin", icon: LayoutDashboard, exact: true },
  {
    name: "Articles",
    href: "/app/admin/articles",
    icon: FileText,
    badge: true,
    subItems: [
      { name: "All Articles", href: "/app/admin/articles" },
      { name: "Pending Review", href: "/app/admin/articles/pending_review", badge: true },
      { name: "Meetings", href: "/app/admin/meetings" },
    ],
  },
  {
    name: "User Management",
    href: "/app/admin/people",
    icon: Users,
    subItems: [
      { name: "People", href: "/app/admin/people" },
      { name: "Groups", href: "/app/admin/groups" },
    ],
  },
  {
    name: "Submissions",
    href: "/app/admin/submissions",
    icon: ClipboardList,
    badge: "applications",
    subItems: [
      { name: "Form Submissions", href: "/app/admin/submissions" },
      { name: "Feedback Requests", href: "/app/admin/feedback_submissions" },
    ],
  },
  {
    name: "Courses",
    href: "/app/admin/courses",
    icon: BookOpen,
    subItems: [
      { name: "Courses", href: "/app/admin/courses" },
      { name: "Tracks", href: "/app/admin/learning_tracks" },
      { name: "Categories", href: "/app/admin/course_categories" },
    ],
  },
  { name: "Marketing", href: "/app/admin/marketing", icon: TrendingUp },
  { name: "Broadcasts", href: "/app/admin/broadcasts", icon: Send },
  // Kingdom Factors books (#674): every book, the flags KF decides, and the
  // files every book shares.
  {
    name: "Books",
    href: "/app/admin/books",
    icon: ShieldCheck,
    subItems: [
      { name: "All books", href: "/app/admin/books" },
      { name: "Book flags", href: "/app/admin/brand_flags" },
      { name: "Brand check rules", href: "/app/admin/brand_check_rules" },
      { name: "Book files", href: "/app/admin/book_files" },
    ],
  },
  {
    name: "Settings",
    href: "/app/admin/settings",
    icon: Settings,
    subItems: [
      { name: "General", href: "/app/admin/settings" },
      { name: "Tutorials", href: "/app/admin/tutorial_videos" },
    ],
  },
]

export function AdminLayout({ children, currentUser, pendingCount = 0, applicationsPendingCount = 0 }) {
  const { url } = usePage()
  const pathname = inertiaUrlPathname(url)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const isDesktop = useMediaQuery("(min-width: 1024px)")

  useEffect(() => {
    if (isDesktop) setSidebarOpen(false)
  }, [isDesktop])

  const matchesPath = (href) => pathname === href || pathname.startsWith(href + "/")

  const isActive = (item) => {
    if (item.exact) return pathname === item.href
    const candidates = item.subItems ? item.subItems.map((s) => s.href) : [item.href]
    return candidates.some(matchesPath)
  }

  // Sub-item active = matches the URL with the longest matching prefix among siblings.
  // Prevents /articles/pending_review from also lighting up the /articles sub-item.
  const isSubActive = (item, sub) => {
    if (!matchesPath(sub.href)) return false
    return !item.subItems.some(
      (other) =>
        other.href !== sub.href &&
        other.href.length > sub.href.length &&
        matchesPath(other.href)
    )
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <ImpersonationBanner />
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-zinc-200 transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-zinc-200">
            <Link href="/" className="flex items-center gap-2">
              <img src={logoBlackText} alt="Kingdom Factor" className="h-8" />
            </Link>
            <button
              className="lg:hidden p-2 rounded-md hover:bg-zinc-100 text-zinc-500"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* User info */}
          <div className="px-4 py-3 border-b border-zinc-200">
            <p className="text-sm font-medium text-zinc-900">
              {currentUser?.name || currentUser?.email}
            </p>
            <p className="text-xs text-zinc-500 capitalize">Administrator</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
            {navigation.map((item) => {
              const active = isActive(item)
              return (
                <div key={item.name}>
                  <Link
                    href={item.href}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      active
                        ? "bg-kf-red text-white"
                        : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.name}
                    {item.badge === true && pendingCount > 0 && (
                      <Badge
                        variant="secondary"
                        className="ml-auto bg-amber-500 text-white"
                      >
                        {pendingCount}
                      </Badge>
                    )}
                    {item.badge === "applications" && applicationsPendingCount > 0 && (
                      <Badge
                        variant="secondary"
                        className="ml-auto bg-amber-500 text-white"
                      >
                        {applicationsPendingCount}
                      </Badge>
                    )}
                    {active && !item.badge && !item.subItems && (
                      <ChevronRight className="ml-auto h-4 w-4" />
                    )}
                  </Link>

                  {item.subItems && active && (
                    <div className="mt-1 ml-7 mb-1 space-y-1 border-l border-zinc-200 pl-3">
                      {item.subItems.map((sub) => {
                        const subActive = isSubActive(item, sub)
                        return (
                          <Link
                            key={sub.href}
                            href={sub.href}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                              subActive
                                ? "font-medium text-kf-red bg-kf-red/5"
                                : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
                            }`}
                          >
                            <span className="flex-1">{sub.name}</span>
                            {sub.badge === true && pendingCount > 0 && (
                              <Badge
                                variant="secondary"
                                className="bg-amber-500 text-white"
                              >
                                {pendingCount}
                              </Badge>
                            )}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </nav>

          {/* Cross-portal links — only show portals the user actually
              belongs to. Admins who don't belong to any group see neither. */}
          {(currentUser?.isCoachOfAnyGroup || currentUser?.isMemberOfAnyGroup) && (
            <>
              <Separator className="mx-3 bg-zinc-200" />
              <div className="px-3 py-4 space-y-1">
                {currentUser?.isCoachOfAnyGroup && (
                  <Link
                    href="/app/coach"
                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
                  >
                    <Users className="h-5 w-5" />
                    Coach Portal
                  </Link>
                )}
                {currentUser?.isMemberOfAnyGroup && (
                  <Link
                    href="/app/member"
                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
                  >
                    <BookOpen className="h-5 w-5" />
                    Member Portal
                  </Link>
                )}
              </div>
            </>
          )}

          {/* Footer */}
          <div className="p-3 border-t border-zinc-200 space-y-1">
            <Link
              href="/account"
              className="flex w-full items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
            >
              <Lock className="h-5 w-5" />
              Account
            </Link>
            <Link
              href="/logout"
              method="post"
              as="button"
              className="flex w-full items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
            >
              <LogOut className="h-5 w-5" />
              Sign Out
            </Link>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-zinc-200 flex items-center px-4 lg:px-6">
          <button
            className="lg:hidden p-2 rounded-md hover:bg-zinc-100 mr-4 text-zinc-500"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">{children}</main>
      </div>

      <FeedbackWidget />
    </div>
  )
}
